import gsap from 'gsap'
import Draggable from 'gsap/Draggable'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import PhotoSwipe from 'photoswipe'
import PhotoSwipeLightbox from 'photoswipe/lightbox'

gsap.registerPlugin(Draggable, ScrollTrigger)

const HEIGHT_DURATION = 0.4
const HEIGHT_EASE = 'power2.out'
const PLAN_ZOOM_SCALES = [1, 2, 3, 4]

function init() {
    initPlans()
    initGallery()
}

function initPlans() {
    const cards = document.querySelectorAll('[data-plan]')
    if (!cards.length) return

    const modalImage = document.querySelector('[data-plan-modal-image]')
    const modalImageWrapper = document.querySelector('[data-plan-modal-image-wrapper]')
    const modalType = document.querySelector('[data-plan-modal-type]')
    const modalFloor = document.querySelector('[data-plan-modal-floor]')
    let activeCard = cards[0]

    // Skip height animation while the window itself is being resized — snap
    // instead, so dragging the browser edge doesn't trigger a chasing tween.
    let resizing = false
    let resizeTimer
    window.addEventListener('resize', () => {
        resizing = true
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(() => (resizing = false), 200)
    })

    // Per-card height controller. A ResizeObserver catches *any* height change —
    // floor image swap, collection switch, a lazily-loaded plan settling, a
    // responsive reflow — animates the card to its new height, and refreshes
    // ScrollTrigger so scroll-driven layout stays in sync.
    const controllers = new Map()

    cards.forEach((card) => {
        let height = card.offsetHeight
        let busy = false

        const animate = (from, to) => {
            if (Math.round(from) === Math.round(to)) {
                ScrollTrigger.refresh()
                return
            }
            busy = true
            gsap.killTweensOf(card, 'height')
            gsap.fromTo(
                card,
                { height: from, overflow: 'hidden' },
                {
                    height: to,
                    duration: HEIGHT_DURATION,
                    ease: HEIGHT_EASE,
                    onComplete: () => {
                        gsap.set(card, { clearProps: 'height,overflow' })
                        height = card.offsetHeight
                        busy = false
                        ScrollTrigger.refresh()
                    },
                }
            )
        }

        const observer = new ResizeObserver(() => {
            if (busy || card.classList.contains('hidden')) return
            const to = card.offsetHeight
            if (Math.round(to) === Math.round(height)) return
            const from = height
            height = to
            if (resizing) {
                ScrollTrigger.refresh()
                return
            }
            animate(from, to)
        })
        observer.observe(card)

        controllers.set(card, {
            // Morph from an explicit starting height (used when swapping cards,
            // where the outgoing card defines where the incoming one starts).
            morphFrom(from) {
                height = card.offsetHeight
                animate(from, height)
            },
        })
    })

    // Resolve once the given source is decoded and cached, so swapping it into a
    // visible <img> lays out at full size immediately (no collapse-while-loading).
    const preload = (src) =>
        new Promise((resolve) => {
            const img = new Image()
            img.onload = img.onerror = () => resolve()
            img.src = src
        })

    // Fade an image out, swap its (preloaded) source, then fade it back in. The
    // height change that follows is picked up once by the ResizeObserver above.
    const swapImage = (image, src, alt) => {
        if (!image) return

        gsap.killTweensOf(image)
        const ready = preload(src)
        gsap.to(image, {
            autoAlpha: 0,
            duration: 0.2,
            ease: 'power1.in',
            onComplete: () => {
                ready.then(() => {
                    image.src = src
                    image.alt = alt
                    gsap.to(image, { autoAlpha: 1, duration: 0.3, ease: 'power1.out' })
                })
            },
        })
    }

    let resetDrag = () => {}
    let zoomAtPoint = () => {}

    const syncModal = () => {
        const image = activeCard.querySelector('[data-plan-image]')
        if (!modalImage || !image) return
        modalImage.src = image.src
        modalImage.alt = image.alt
        resetDrag()
        if (modalType) modalType.textContent = activeCard.dataset.planType ?? ''
        const activeFloor = activeCard.querySelector('[data-plan-floor].active')
        if (modalFloor) modalFloor.textContent = activeFloor?.dataset.planName ?? ''
    }

    const showCard = (slug) => {
        const next = [...cards].find((card) => card.dataset.plan === slug)
        if (!next || next === activeCard) return

        const prev = activeCard
        activeCard = next

        const prevHeight = prev.offsetHeight
        const nextImage = next.querySelector('[data-plan-image]')
        const ready = nextImage ? preload(nextImage.src) : Promise.resolve()

        gsap.killTweensOf(cards)
        cards.forEach((card) => {
            if (card === prev || card === next) return
            card.classList.add('hidden')
            gsap.set(card, { clearProps: 'opacity,visibility,transform,height,overflow' })
        })

        gsap.to(prev, {
            autoAlpha: 0,
            duration: 0.25,
            ease: 'power1.in',
            onComplete: () => {
                prev.classList.add('hidden')
                gsap.set(prev, { clearProps: 'opacity,visibility,height,overflow' })

                ready.then(() => {
                    next.classList.remove('hidden')
                    gsap.fromTo(
                        next,
                        { autoAlpha: 0, y: 12 },
                        { autoAlpha: 1, y: 0, duration: 0.35, ease: 'power1.out', clearProps: 'transform' }
                    )
                    controllers.get(next).morphFrom(prevHeight)
                    syncModal()
                })
            },
        })
    }

    const setFloor = (card, btn) => {
        card.querySelectorAll('[data-plan-floor]').forEach((b) => b.classList.toggle('active', b === btn))

        const alt = `Floor plan — ${btn.dataset.planName}`
        swapImage(card.querySelector('[data-plan-image]'), btn.dataset.planSrc, alt)
        if (card === activeCard) {
            zoomLevel = 0
            swapImage(modalImage, btn.dataset.planSrc, alt)
            resetDrag()
            if (modalFloor) modalFloor.textContent = btn.dataset.planName
        }
    }

    cards.forEach((card) => {
        card.querySelectorAll('[data-plan-floor]').forEach((btn) => {
            btn.addEventListener('click', () => setFloor(card, btn))
        })
    })

    const collectionGroups = document.querySelectorAll('[data-collection-tabs]')

    collectionGroups.forEach((group) => {
        const btns = group.querySelectorAll('[data-collection-tab]')

        btns.forEach((btn) => {
            btn.addEventListener('click', () => {
                btns.forEach((b) => b.classList.toggle('active', b === btn))
                showCard(btn.dataset.collectionTab)
            })
        })
    })

    const phaseBtns = document.querySelectorAll('[data-phase-tab]')

    phaseBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
            phaseBtns.forEach((b) => b.classList.toggle('active', b === btn))

            collectionGroups.forEach((group) => {
                const isActive = group.dataset.collectionTabs === btn.dataset.phaseTab
                group.classList.toggle('hidden', !isActive)
                if (!isActive) return

                gsap.fromTo(
                    group,
                    { autoAlpha: 0, y: 8 },
                    { autoAlpha: 1, y: 0, duration: 0.35, ease: 'power1.out', clearProps: 'transform' }
                )

                const current =
                    group.querySelector('[data-collection-tab].active') ||
                    group.querySelector('[data-collection-tab]')
                if (current) showCard(current.dataset.collectionTab)
            })
        })
    })

    // Modal prev/next cycle through the active card's floors
    const stepFloor = (dir) => {
        const floors = [...activeCard.querySelectorAll('[data-plan-floor]')]
        if (floors.length < 2) return

        const current = floors.findIndex((b) => b.classList.contains('active'))
        setFloor(activeCard, floors[(current + dir + floors.length) % floors.length])
    }

    const modalPrev = document.querySelector('[data-plan-modal-prev]')
    const modalNext = document.querySelector('[data-plan-modal-next]')

    if (modalPrev) modalPrev.addEventListener('click', () => stepFloor(-1))
    if (modalNext) modalNext.addEventListener('click', () => stepFloor(1))

    // Expand / collapse modal
    const modalContainer = document.querySelector('[data-plan-modal-container]')
    const expandBtn = document.querySelector('[data-plan-modal-expand]')
    const collapseBtn = document.querySelector('[data-plan-modal-collapse]')
    const zoomControls = document.querySelector('[data-plan-modal-zoom-controls]')
    const zoomTrigger = document.querySelector('[data-plan-modal-zoom-trigger]')
    const zoomMenu = document.querySelector('[data-plan-modal-zoom-menu]')
    const zoomLabel = document.querySelector('[data-plan-modal-zoom-label]')
    const zoomOptions = document.querySelectorAll('[data-plan-modal-zoom-level]')
    let zoomLevel = 0
    let wheelZoomLocked = false

    const closeZoomMenu = () => {
        zoomMenu?.classList.remove('is-open')
        zoomTrigger?.setAttribute('aria-expanded', 'false')
    }

    const setZoomLevel = (level, focalPoint = null) => {
        const nextLevel = Math.max(0, Math.min(PLAN_ZOOM_SCALES.length - 1, level))
        closeZoomMenu()
        if (nextLevel === zoomLevel) return

        zoomLevel = nextLevel
        if (focalPoint) {
            zoomAtPoint(focalPoint)
            return
        }
        resetDrag()
    }

    if (expandBtn && collapseBtn && modalContainer) {
        expandBtn.addEventListener('click', () => setZoomLevel(zoomLevel + 1))
        collapseBtn.addEventListener('click', () => setZoomLevel(zoomLevel - 1))
    }

    zoomTrigger?.addEventListener('click', () => {
        const isOpen = zoomMenu?.classList.toggle('is-open') ?? false
        zoomTrigger.setAttribute('aria-expanded', String(isOpen))
    })

    zoomOptions.forEach((option) => {
        option.addEventListener('click', () => {
            const scale = Number(option.dataset.planModalZoomLevel)
            const level = PLAN_ZOOM_SCALES.indexOf(scale)
            if (level !== -1) setZoomLevel(level)
        })
    })

    document.addEventListener('click', (event) => {
        if (!zoomControls?.contains(event.target)) closeZoomMenu()
    })

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || zoomTrigger?.getAttribute('aria-expanded') !== 'true') return
        closeZoomMenu()
        zoomTrigger.focus()
    })

    modalImageWrapper?.addEventListener(
        'wheel',
        (event) => {
            event.preventDefault()
            if (wheelZoomLocked || event.deltaY === 0) return

            wheelZoomLocked = true
            setZoomLevel(zoomLevel + (event.deltaY > 0 ? -1 : 1), {
                x: event.clientX,
                y: event.clientY,
            })
            window.setTimeout(() => {
                wheelZoomLocked = false
            }, 180)
        },
        { passive: false }
    )

    // Drag-to-pan on modal image
    if (modalImage) {
        const getZoomScale = () => PLAN_ZOOM_SCALES[zoomLevel]
        const canDrag = () => zoomLevel > 0
        const getDragBounds = (zoomScale = getZoomScale()) => {
            if (!modalImageWrapper) return { minX: 0, maxX: 0, minY: 0, maxY: 0 }

            const maxX = Math.max(0, (modalImage.offsetWidth * zoomScale - modalImageWrapper.clientWidth) / 2)
            const maxY = Math.max(
                0,
                (modalImage.offsetHeight * zoomScale - modalImageWrapper.clientHeight) / 2
            )

            return {
                minX: -maxX,
                maxX,
                minY: -maxY,
                maxY,
            }
        }

        const draggable = Draggable.create(modalImage, {
            type: 'x,y',
            bounds: getDragBounds(),
            edgeResistance: 1,
            zIndexBoost: false,
            cursor: 'grab',
            activeCursor: 'grabbing',
            onPress() {
                this.applyBounds(getDragBounds())
            },
        })[0]

        const syncZoomControls = () => {
            const zoomScale = getZoomScale()

            modalContainer?.classList.toggle('is-expanded', canDrag())
            if (expandBtn) expandBtn.disabled = zoomLevel === PLAN_ZOOM_SCALES.length - 1
            if (collapseBtn) collapseBtn.disabled = zoomLevel === 0
            if (zoomLabel) zoomLabel.textContent = `${zoomScale}x`
            zoomOptions.forEach((option) => {
                option.setAttribute(
                    'aria-selected',
                    String(Number(option.dataset.planModalZoomLevel) === zoomScale)
                )
            })
        }

        const applyZoom = (x, y) => {
            const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 0.35

            syncZoomControls()

            gsap.killTweensOf(modalImage, 'x,y,scale')
            gsap.to(modalImage, {
                x,
                y,
                scale: getZoomScale(),
                duration,
                ease: 'power2.out',
                onUpdate: () => draggable.update(),
            })

            if (canDrag()) {
                draggable.applyBounds(getDragBounds())
                draggable.enable()
                modalImage.style.cursor = 'grab'
                return
            }

            draggable.disable()
            modalImage.style.cursor = ''
        }

        resetDrag = () => applyZoom(0, 0)

        zoomAtPoint = ({ x, y }) => {
            if (!modalImageWrapper) {
                resetDrag()
                return
            }

            const wrapperRect = modalImageWrapper.getBoundingClientRect()
            const pointerX = x - (wrapperRect.left + wrapperRect.width / 2)
            const pointerY = y - (wrapperRect.top + wrapperRect.height / 2)
            const currentScale = Number(gsap.getProperty(modalImage, 'scale')) || 1
            const currentX = Number(gsap.getProperty(modalImage, 'x')) || 0
            const currentY = Number(gsap.getProperty(modalImage, 'y')) || 0
            const scaleRatio = getZoomScale() / currentScale
            const bounds = getDragBounds()
            const nextX = gsap.utils.clamp(
                bounds.minX,
                bounds.maxX,
                pointerX - (pointerX - currentX) * scaleRatio
            )
            const nextY = gsap.utils.clamp(
                bounds.minY,
                bounds.maxY,
                pointerY - (pointerY - currentY) * scaleRatio
            )

            applyZoom(nextX, nextY)
        }

        modalImage.setAttribute('draggable', 'false')
        resetDrag()
    }

    // Reset expanded state + drag when modal closes
    const planModal = document.getElementById('popup-plan')
    if (planModal) {
        new MutationObserver(() => {
            if (planModal.getAttribute('aria-hidden') !== 'true') return
            zoomLevel = 0
            closeZoomMenu()
            resetDrag()
        }).observe(planModal, { attributes: true, attributeFilter: ['aria-hidden'] })
    }
}

function initGallery() {
    const gallery = document.querySelector('[data-gallery]')
    if (!gallery) return

    const prev = document.querySelector('[data-gallery-prev]')
    const next = document.querySelector('[data-gallery-next]')

    if (prev) prev.addEventListener('click', () => gallery.swiper && gallery.swiper.slidePrev())
    if (next) next.addEventListener('click', () => gallery.swiper && gallery.swiper.slideNext())

    initGalleryLightbox(gallery)
}

// PhotoSwipe lightbox for the gallery. Slides are opened programmatically so
// Swiper's loop mode (which reorders and may clone slides in the DOM) doesn't
// scramble or duplicate the lightbox entries. Each slide's original position is
// read from `data-swiper-slide-index` when present, falling back to DOM order.
function initGalleryLightbox(gallery) {
    const lightbox = new PhotoSwipeLightbox({
        pswpModule: PhotoSwipe,
        padding: { top: 32, bottom: 32, left: 32, right: 32 },
    })
    lightbox.init()

    const slideIndex = (slide, slides) => {
        const attr = slide.getAttribute('data-swiper-slide-index')
        return attr !== null ? Number(attr) : slides.indexOf(slide)
    }

    // Build a de-duplicated, index-ordered data source from the real slides.
    const buildDataSource = () => {
        const slides = [...gallery.querySelectorAll('swiper-slide')]
        const byIndex = new Map()

        slides.forEach((slide) => {
            const index = slideIndex(slide, slides)
            const img = slide.querySelector('img')
            if (index < 0 || !img || byIndex.has(index)) return

            byIndex.set(index, {
                // `element` + `msrc` let PhotoSwipe animate the open/close as a
                // zoom out of (and back into) the clicked thumbnail.
                element: img,
                msrc: img.currentSrc || img.src,
                src: img.currentSrc || img.src,
                width: Number(img.getAttribute('width')),
                height: Number(img.getAttribute('height')),
                alt: img.alt,
            })
        })

        return [...byIndex.keys()].sort((a, b) => a - b).map((key) => byIndex.get(key))
    }

    // Keep the carousel in sync with the lightbox so the closing zoom animates
    // back to the slide currently being viewed, not the one originally clicked.
    lightbox.on('change', () => {
        const { pswp } = lightbox
        if (pswp && gallery.swiper) gallery.swiper.slideToLoop(pswp.currIndex, 0)
    })

    gallery.addEventListener('click', (event) => {
        const slide = event.target.closest('swiper-slide')
        if (!slide || !gallery.contains(slide)) return

        const slides = [...gallery.querySelectorAll('swiper-slide')]
        const index = slideIndex(slide, slides)
        if (Number.isNaN(index) || index < 0) return

        lightbox.loadAndOpen(index, buildDataSource())
    })
}

export default init
